import * as THREE from 'three';
import { hexToWorld, type BattleState, type DungeonLayout, type MapDef } from '@somnhold/sim';
import { emis } from '../materials.ts';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import type { CryptBuild } from './crypt.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The dragon's bones (La Orilla 4, docs/SAGAS.md): the crypt re-dressed as the real dragon's skeleton on the sea floor
 * the sea left bare. Every room is a space between colossal blackened ribs that rise from its rim and curve in (along
 * the walls, never over the middle, so the board stays readable), with coral and weed on them and ink trickling down
 * their seams; the floor is wet sand with pools; vertebrae run down the corridors between rooms, the spine.
 *
 * Two fights. «Entre las costillas» (no flood): the first room is the sea floor where they came down, the last hall the
 * chest, with the heart (a red stone the size of a barrel, an ember glowing inside) on the breastbone, and the huge
 * split scale the first Vigil's trap cracked. «La vuelta» (`up`, sim/battle/drinker.ts): they start in the chest, the
 * sea comes in there and floods room by room to the last one, a rock rising out of the seabed with the pale way out on
 * it. The water: one merged sheet over the floor cells carrying how far along the rooms' chain each vertex is (rooms in
 * order, as `along` with `up`), shown up to the front with a foam band; the heart carried over its carrier's shoulders,
 * dropped with a warm pulsing ring, taken by the sea with a hiss of steam.
 *
 * Draw calls: the dressing (one merged mesh), the wet bits (one, glossy), the water (one), the heart (three small
 * meshes), a glow layer. No lights of its own: the crypt's room lights are re-coloured.
 */

const BONE = ['#6a5c56', '#5a4e4a', '#76675e'];
const BONE_LIT = '#8a7a6e';
const SAND = '#5e5547';
const SAND_DARK = '#4a4338';
const POOL = '#1f3a46';
const CORAL = ['#b8584a', '#c97a52', '#9a4a5e', '#d08a6a'];
const WEED = ['#2f4a34', '#3a5a3a', '#26402e'];
const INK = '#2a1838';
const ROCK = ['#24262b', '#2c2e33', '#1c1d21'];
const SHELL = '#b8ad98';
const SCALE = '#121014';
const RED = new THREE.Color('#ff4f3a');
const EMBER = new THREE.Color('#ffb070');
const EMBER_CORE = new THREE.Color('#fff0d8');
const INK_GLOW = new THREE.Color('#6a3aa8');
const COOL = new THREE.Color('#cfe8ff');

type Pt = { x: number; y: number };

/** The flood's water: shown up to the front, foam at its edge (as the pipes'). */
const waterMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { front: { value: -1 }, time: { value: 0 }, foam: { value: 1000 } },
    vertexShader: /* glsl */ `
      attribute float along;
      varying float vAlong;
      varying vec2 vXZ;
      uniform float time;
      void main() {
        vAlong = along;
        vXZ = position.xz;
        vec3 p = position;
        p.y += 0.03 * sin(p.x * 2.1 + time * 2.3) * cos(p.z * 1.7 - time * 1.9);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying float vAlong;
      varying vec2 vXZ;
      uniform float front;
      uniform float time;
      uniform float foam;
      void main() {
        if (vAlong > front) discard;
        float edge = smoothstep(front - foam, front, vAlong);
        float rip = 0.5 + 0.5 * sin(vXZ.x * 2.9 + vXZ.y * 2.5 + time * 3.2);
        vec3 deep = mix(vec3(0.07, 0.20, 0.27), vec3(0.13, 0.32, 0.40), rip * 0.5);
        vec3 col = mix(deep, vec3(0.93, 0.97, 1.0), edge * edge);
        gl_FragColor = vec4(col, mix(0.8, 0.96, edge));
      }`,
  });

/** The dragon's heart, for the fight: a red stone with an ember inside (shell, glow core, ember). */
export const heartMeshes = (): { group: THREE.Group; mats: THREE.Material[]; geos: THREE.BufferGeometry[] } => {
  const group = new THREE.Group();
  const shellG = new THREE.DodecahedronGeometry(0.42, 1);
  const pos = shellG.getAttribute('position');
  // Lumpy, like a stone, not a gem.
  for (let i = 0; i < pos.count; i++) {
    const k = 1 + 0.08 * Math.sin(pos.getX(i) * 9.1 + pos.getY(i) * 7.3) + 0.06 * Math.cos(pos.getZ(i) * 8.7);
    pos.setXYZ(i, pos.getX(i) * k, pos.getY(i) * k * 0.92, pos.getZ(i) * k);
  }
  shellG.computeVertexNormals();
  const shell = new THREE.MeshStandardMaterial({ color: '#5a1410', roughness: 0.55, metalness: 0.1, flatShading: true, emissive: '#7a1a10', emissiveIntensity: 0.9, transparent: true, opacity: 0.92 });
  const coreG = new THREE.IcosahedronGeometry(0.2, 0);
  const core = emis('#ffd2a0', '#ff8a3a', 2.2);
  const crackG = new THREE.TorusGeometry(0.36, 0.018, 4, 18);
  const crack = emis('#ffb070', '#ff6a2a', 1.6);
  group.add(new THREE.Mesh(shellG, shell));
  group.add(new THREE.Mesh(coreG, core));
  const c1 = new THREE.Mesh(crackG, crack);
  c1.rotation.set(0.6, 0.3, 0);
  const c2 = new THREE.Mesh(crackG, crack);
  c2.rotation.set(-0.4, 1.2, 0.5);
  c2.scale.setScalar(0.9);
  group.add(c1, c2);
  group.name = 'dragon-heart';
  return { group, mats: [shell, core, crack], geos: [shellG, coreG, crackG] };
};

export class RibsLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(320);
  private water: THREE.Mesh;
  private waterM: THREE.ShaderMaterial;
  /** The rooms' chain the water comes along (sim units), its segments' lengths. */
  private chain: Pt[] = [];
  private lens: number[] = [];
  /** The chest's heart spot (world), the rock's (world), the ink seams' glints. */
  private chest = new THREE.Vector3();
  private rock = new THREE.Vector3();
  private ink: THREE.Vector3[] = [];
  private heart: THREE.Group;
  private now = 0;
  private v = new THREE.Vector3();
  private lastHeart = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    crypt: CryptBuild,
    rooms: THREE.PointLight[],
    private up: boolean,
  ) {
    this.group.name = 'ribs';
    const L = map.dungeon as DungeonLayout;
    const S = WORLD_PER_HEX;
    const last = L.rooms.length - 1;
    // The chest: fight 1's last hall, fight 2's first room. The rock: fight 2's last room.
    const chestRoom = up ? 0 : last;
    crypt.runes.visible = false;
    // Fight 1's last hall is the chest, not a way out: the heart stands where the exit's shaft would.
    if (!up && crypt.exit) crypt.exit.group.visible = false;
    // Cold sea light in every room; the chest burns red.
    rooms.forEach((l, i) => {
      l.color.set(i === chestRoom ? 0xff8a5a : 0xb8d0e0);
      l.intensity = i === chestRoom ? 16 : 20;
    });
    const coldFlame = emis('#cfe8ff', '#7fb8ff', 1.2, { transparent: true, opacity: 0.9, depthWrite: false });
    for (const f of crypt.flames) {
      (f.mesh as THREE.Mesh).material = coldFlame;
      (f.glow.material as THREE.SpriteMaterial).color.set(f.room === chestRoom ? '#ff6a3a' : '#7fb8ff');
    }
    this.mats.push(coldFlame);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const rockG = new THREE.DodecahedronGeometry(0.5, 0);
    const disc = new THREE.CircleGeometry(0.5, 18).rotateX(-Math.PI / 2);
    const vert = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const cone = new THREE.ConeGeometry(0.5, 1, 6);
    const kit = new Kit();
    const wet = new Kit();
    // The chain of room centres: the spine, and the way the water comes (fight 2).
    const pts = L.rooms.map((r) => hexToWorld(r.q, r.r));
    L.rooms.forEach((room, i) => {
      const w = pts[i] as Pt;
      const c = ctx.at(w.x, w.y, 0);
      const rw = room.radius * S;
      // The spine's way through this room (towards the next room, or from the one before).
      const nb = pts[i + 1] ?? pts[i - 1] ?? { x: w.x + 1000, y: w.y };
      const dir = Math.atan2(nb.x - w.x, nb.y - w.y);
      this.floor(kit, wet, disc, rockG, c, rw, i);
      this.ribs(kit, wet, cone, rockG, box, c, rw, dir, i, i === chestRoom ? 6 : 4);
      if (i === chestRoom) this.chestRoom(kit, wet, box, rockG, c, rw, dir);
      else if (up && i === last) this.rockRoom(wet, rockG, c, rw);
      else if (!up && i === 0) this.seaFloor(kit, wet, rockG, cone, c, rw);
    });
    // Vertebrae down the corridors between rooms: the spine.
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1] as Pt;
      const b = pts[i] as Pt;
      const ra = (L.rooms[i - 1]?.radius ?? 2) * 1000;
      const rb = (L.rooms[i]?.radius ?? 2) * 1000;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const yaw = Math.atan2(b.x - a.x, b.y - a.y);
      for (let d = ra + 400; d < len - rb - 200; d += 900) {
        const p = ctx.at(a.x + ((b.x - a.x) * d) / len, a.y + ((b.y - a.y) * d) / len, 0);
        kit.add(vert, BONE[(d / 900) % 3 | 0] as string, place(p.x, p.y + 0.06, p.z, yaw, new THREE.Vector3(0.62, 0.12, 0.62)));
        kit.add(box, BONE[1] as string, place(p.x, p.y + 0.08, p.z, yaw, new THREE.Vector3(1.1, 0.06, 0.12)));
        if (((d / 900) | 0) % 2 === 0) wet.add(rockG, CORAL[((d / 900) | 0) % 4] as string, place(p.x + 0.3, p.y + 0.12, p.z, d, 0.12));
      }
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.05, flatShading: true });
    const wetMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.2, metalness: 0.3, flatShading: true });
    this.mats.push(mat, wetMat);
    for (const [k, m, n] of [
      [kit, mat, 'ribs-dress'],
      [wet, wetMat, 'ribs-wet'],
    ] as const) {
      const mesh = k.build(m, n);
      if (!mesh) continue;
      mesh.receiveShadow = true;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }

    // The water (fight 2): rooms in order, the chest first.
    this.chain = up ? pts : pts.slice().reverse();
    for (let i = 1; i < this.chain.length; i++) {
      const a = this.chain[i - 1] as Pt;
      const b = this.chain[i] as Pt;
      this.lens.push(Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)));
    }
    const pos: number[] = [];
    const along: number[] = [];
    const rad = S / Math.sqrt(3);
    const k = S / 1000;
    if (up)
      for (const h of L.floor) {
        const w = hexToWorld(h.q, h.r);
        const cx = w.x * k;
        const cz = w.y * k;
        const y = ctx.ground(cx, cz) + 0.17;
        const corner = (n: number): [number, number] => {
          const a = (Math.PI / 180) * (60 * n - 30);
          return [cx + Math.cos(a) * rad * 1.02, cz + Math.sin(a) * rad * 1.02];
        };
        const ca = this.along(cx / k, cz / k);
        for (let n = 0; n < 6; n++) {
          const [ax, az] = corner(n);
          const [bx, bz] = corner((n + 1) % 6);
          pos.push(cx, y, cz, bx, y, bz, ax, y, az);
          along.push(ca, this.along(bx / k, bz / k), this.along(ax / k, az / k));
        }
      }
    const wg = new THREE.BufferGeometry();
    wg.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    wg.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
    wg.computeBoundingSphere();
    this.geos.push(wg);
    this.waterM = waterMat();
    this.mats.push(this.waterM);
    this.water = new THREE.Mesh(wg, this.waterM);
    this.water.name = 'ribs-flood';
    this.water.renderOrder = 3;
    this.water.frustumCulled = false;
    this.water.visible = false;
    this.group.add(this.water);

    // The heart: on the breastbone in the chest (fight 1, or fight 2 if left there); carried or dropped in fight 2.
    const h = heartMeshes();
    this.heart = h.group;
    this.mats.push(...h.mats);
    this.geos.push(...h.geos);
    this.heart.position.copy(this.chest).setY(this.chest.y + 0.75);
    this.group.add(this.heart);

    this.group.add(this.glow.mesh);
    for (const g of [box, rockG, disc, vert, cone]) g.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** Wet sand over the room's floor, darker at the rim, pools and shells, weed. */
  private floor(kit: Kit, wet: Kit, disc: THREE.BufferGeometry, rock: THREE.DodecahedronGeometry, c: THREE.Vector3, rw: number, i: number): void {
    const g = c.y;
    kit.add(disc, SAND, place(c.x, g + 0.015, c.z, 0, new THREE.Vector3(rw * 1.95, 1, rw * 1.95)));
    kit.add(new THREE.RingGeometry(0.4, 0.5, 24).rotateX(-Math.PI / 2), SAND_DARK, place(c.x, g + 0.018, c.z, 0, new THREE.Vector3(rw * 2, 1, rw * 2)));
    for (let k = 0; k < 4 + (i % 3); k++) {
      const a = k * 2.1 + i * 0.7;
      const d = rw * (0.25 + ((k * 3) % 5) * 0.1);
      wet.add(disc, POOL, place(c.x + Math.cos(a) * d, g + 0.024, c.z + Math.sin(a) * d, a, new THREE.Vector3(0.5 + (k % 3) * 0.22, 1, 0.32 + (k % 2) * 0.16)));
    }
    for (let k = 0; k < 6; k++) {
      const a = k * 1.7 + i;
      const d = rw * (0.3 + (k % 4) * 0.13);
      kit.add(rock, SHELL, place(c.x + Math.cos(a) * d, g + 0.03, c.z + Math.sin(a) * d, a, new THREE.Vector3(0.08, 0.03, 0.06)));
    }
  }

  /**
   * The ribs: `n` a side, rising from the room's rim across the spine's way and curving in over the edge (never over
   * the middle), blackened, coral crusted on them, weed hanging, ink trickling down a seam.
   */
  private ribs(kit: Kit, wet: Kit, cone: THREE.ConeGeometry, rock: THREE.DodecahedronGeometry, box: THREE.BoxGeometry, c: THREE.Vector3, rw: number, dir: number, i: number, n: number): void {
    const g = c.y;
    // Across the spine: the ribs stand on both sides of it.
    const ax = Math.cos(dir);
    const az = -Math.sin(dir);
    const fx = Math.sin(dir);
    const fz = Math.cos(dir);
    for (const side of [-1, 1]) {
      for (let k = 0; k < n; k++) {
        const t = n === 1 ? 0 : (k / (n - 1)) * 2 - 1;
        const along = t * rw * 0.7;
        const root = new THREE.Vector3(c.x + fx * along + ax * side * rw * 1.02, g, c.z + fz * along + az * side * rw * 1.02);
        const h = 2.0 + 0.5 * Math.cos(t * 1.4) + ((k + i) % 2) * 0.2;
        const inward = rw * (0.55 + 0.1 * Math.cos(t * 1.4));
        const pts: THREE.Vector3[] = [];
        for (let s = 0; s <= 6; s++) {
          const u = s / 6;
          // Straight up out of the sand, then curling in over the edge towards the spine, like a rib cage's.
          const out = 1 - Math.cos((u * Math.PI) / 2);
          const lift = Math.sin((u * Math.PI) / 2) * h;
          pts.push(new THREE.Vector3(root.x - ax * side * inward * out, g + lift, root.z - az * side * inward * out));
        }
        const curve = new THREE.CatmullRomCurve3(pts);
        const thick = 0.32 - Math.abs(t) * 0.05;
        const tube = new THREE.TubeGeometry(curve, 10, thick, 6, false);
        // Taper to the tip.
        const p = tube.getAttribute('position');
        for (let q = 0; q < p.count; q++) {
          const seg = Math.floor(q / 7) / 10;
          const tip = 1 - seg * 0.55;
          const cx = curve.getPoint(Math.min(1, seg));
          p.setXYZ(q, cx.x + (p.getX(q) - cx.x) * tip, cx.y + (p.getY(q) - cx.y) * tip, cx.z + (p.getZ(q) - cx.z) * tip);
        }
        tube.computeVertexNormals();
        kit.add(tube, BONE[(k + i + (side > 0 ? 1 : 0)) % 3] as string, new THREE.Matrix4());
        tube.dispose();
        // A knuckle where it leaves the sand, sand heaped round it.
        kit.add(rock, BONE_LIT, place(root.x, g + 0.12, root.z, k, 0.34));
        kit.add(rock, SAND_DARK, place(root.x, g + 0.03, root.z, k + 1, new THREE.Vector3(0.6, 0.12, 0.6)));
        // Coral crusted on its lower half; weed hanging off the curve.
        for (let s = 0; s < 3; s++) {
          const q = curve.getPoint(0.12 + s * 0.14 + ((k + s) % 2) * 0.05);
          wet.add(rock, CORAL[(k + s + i) % 4] as string, place(q.x, q.y, q.z, s + k, 0.09 + ((s + k) % 3) * 0.03));
        }
        for (let s = 0; s < 2; s++) {
          const q = curve.getPoint(0.55 + s * 0.18);
          kit.add(cone, WEED[(k + s) % 3] as string, place(q.x, q.y - 0.22, q.z, s, new THREE.Vector3(0.06, 0.45, 0.06), Math.PI));
        }
        // The ink seam: a dark violet trickle down its inner face, glinting.
        const m = curve.getPoint(0.35);
        kit.add(box, INK, place(m.x, (m.y + g) / 2, m.z, dir, new THREE.Vector3(0.05, m.y - g, 0.05)));
        if ((k + i) % 2 === 0) this.ink.push(new THREE.Vector3(m.x, m.y * 0.5 + g * 0.5, m.z));
      }
    }
  }

  /** The chest: a broad breastbone ridge across the middle, the heart on it, the split scale leaning on a rib. */
  private chestRoom(kit: Kit, wet: Kit, box: THREE.BoxGeometry, rock: THREE.DodecahedronGeometry, c: THREE.Vector3, rw: number, dir: number): void {
    const g = c.y;
    // The breastbone: a long low keel across the spine's way, its plates.
    for (let k = -2; k <= 2; k++) {
      const x = c.x + Math.cos(dir) * k * 0.45;
      const z = c.z - Math.sin(dir) * k * 0.45;
      kit.add(box, BONE[(k + 3) % 3] as string, place(x, g + 0.14 - Math.abs(k) * 0.03, z, dir, new THREE.Vector3(0.4, 0.28 - Math.abs(k) * 0.05, 1.1)));
    }
    // The cradle the heart sits in: ribs' tips meeting, coral, a scorched ring of sand.
    kit.add(new THREE.RingGeometry(0.5, 1.2, 20).rotateX(-Math.PI / 2), '#2a1a14', place(c.x, g + 0.02, c.z));
    for (let k = 0; k < 5; k++) wet.add(rock, CORAL[k % 4] as string, place(c.x + Math.cos(k * 1.3) * 0.6, g + 0.2, c.z + Math.sin(k * 1.3) * 0.6, k, 0.12));
    this.chest.set(c.x, g + 0.28, c.z);
    // The huge scale, split from side to side, leaning on the rim: black, glassy, burnt at the crack.
    const sx = c.x + Math.sin(dir) * rw * 0.62;
    const sz = c.z + Math.cos(dir) * rw * 0.62;
    const yaw = dir + Math.PI / 2;
    const half = new THREE.CircleGeometry(0.9, 7, 0, Math.PI);
    wet.add(half, SCALE, place(sx - Math.cos(yaw) * 0.08, g + 0.85, sz + Math.sin(yaw) * 0.08, yaw, new THREE.Vector3(1, 1.1, 1), -0.35, 0.12));
    wet.add(half, SCALE, place(sx + Math.cos(yaw) * 0.1, g + 0.8, sz - Math.sin(yaw) * 0.1, yaw, new THREE.Vector3(1, 1.05, 1), -0.35, Math.PI - 0.2));
    kit.add(box, '#4a1a10', place(sx, g + 0.6, sz, yaw, new THREE.Vector3(0.05, 1.2, 0.04), -0.35));
    half.dispose();
  }

  /** The first room of fight 1: the sea floor they came down onto, open, stranded weed and a few rocks. */
  private seaFloor(kit: Kit, wet: Kit, rock: THREE.DodecahedronGeometry, cone: THREE.ConeGeometry, c: THREE.Vector3, rw: number): void {
    const g = c.y;
    for (let k = 0; k < 8; k++) {
      const a = k * 0.8;
      const d = rw * (0.5 + (k % 3) * 0.15);
      wet.add(rock, ROCK[k % 3] as string, place(c.x + Math.cos(a) * d, g + 0.1, c.z + Math.sin(a) * d, k, 0.2 + (k % 3) * 0.08));
      kit.add(cone, WEED[k % 3] as string, place(c.x + Math.cos(a + 0.4) * d * 0.8, g + 0.12, c.z + Math.sin(a + 0.4) * d * 0.8, k, new THREE.Vector3(0.05, 0.25, 0.05)));
    }
  }

  /** Fight 2's last room: a rock rising out of the seabed, wet, the way out on top of it. */
  private rockRoom(wet: Kit, rock: THREE.DodecahedronGeometry, c: THREE.Vector3, rw: number): void {
    const g = c.y;
    // The rock's flat, wet top in the middle (the way out stands on it), a ring of boulders round its edge.
    wet.add(new THREE.CylinderGeometry(0.5, 0.56, 1, 9), ROCK[1] as string, place(c.x, g + 0.06, c.z, 0.3, new THREE.Vector3(rw * 0.95, 0.12, rw * 0.95)));
    for (let k = 0; k < 9; k++) {
      const a = (k / 9) * Math.PI * 2 + 0.2;
      const s = 0.28 + (k % 3) * 0.08;
      wet.add(rock, ROCK[(k + 1) % 3] as string, place(c.x + Math.cos(a) * rw * 0.5, g + 0.12 + s * 0.2, c.z + Math.sin(a) * rw * 0.5, k, new THREE.Vector3(s, s * 0.6, s)));
    }
    for (let k = 0; k < 14; k++) {
      const a = (k / 14) * Math.PI * 2;
      const d = rw * (0.62 + (k % 3) * 0.08);
      const s = 0.5 + (k % 4) * 0.14;
      wet.add(rock, ROCK[k % 3] as string, place(c.x + Math.cos(a) * d, g + s * 0.3, c.z + Math.sin(a) * d, k * 1.3, new THREE.Vector3(s, s * 0.8, s)));
    }
    this.rock.set(c.x, g, c.z);
  }

  /** How far along the water's way (sim units, from its start) a sim point is: as sim `along` (with `up`). */
  private along(x: number, y: number): number {
    let best = Infinity;
    let at = 0;
    let run = 0;
    for (let i = 1; i < this.chain.length; i++) {
      const a = this.chain[i - 1] as Pt;
      const b = this.chain[i] as Pt;
      const len = this.lens[i - 1] as number;
      let t = ((x - a.x) * (b.x - a.x) + (y - a.y) * (b.y - a.y)) / len;
      t = Math.max(0, Math.min(len, t));
      const px = a.x + ((b.x - a.x) * t) / len;
      const py = a.y + ((b.y - a.y) * t) / len;
      const d = (x - px) ** 2 + (y - py) ** 2;
      if (d < best) {
        best = d;
        at = run + t;
      }
      run += len;
    }
    return at;
  }

  private pointAt(d: number): { p: Pt; dx: number; dy: number } {
    let run = 0;
    for (let i = 1; i < this.chain.length; i++) {
      const a = this.chain[i - 1] as Pt;
      const b = this.chain[i] as Pt;
      const len = this.lens[i - 1] as number;
      if (d <= run + len || i === this.chain.length - 1) {
        const t = Math.max(0, Math.min(1, (d - run) / len));
        return { p: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, dx: (b.x - a.x) / len, dy: (b.y - a.y) / len };
      }
      run += len;
    }
    return { p: this.chain[0] ?? { x: 0, y: 0 }, dx: 0, dy: 1 };
  }

  /** Every fresh tick: ink drips off the bones, the sea's roar and spray, the heart's ring, its loss. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    if (s.tick % (low ? 10 : 5) === 0 && this.ink.length) {
      const p = this.ink[(s.tick >> 2) % this.ink.length] as THREE.Vector3;
      this.ctx.vfx.burst(p, { n: 1, color: '#3a2258', speed: 0.002, up: -0.01, size: 0.04, life: 18, gravity: 0.006, spread: 0.03 });
    }
    // The ember breathes sparks while the heart is in sight.
    if (this.heart.visible && s.tick % (low ? 8 : 4) === 0) this.ctx.vfx.burst(this.heart.position, { n: 1, color: '#ffb070', speed: 0.01, up: 0.03, size: 0.04, life: 18, gravity: -0.003, spread: 0.2 });
    for (const e of s._events ?? []) {
      if (e.t !== 'drinker') continue;
      if (e.kind === 'wave' || e.kind === 'burst') {
        // The sea coming in where they came down: a roar, a wall of foam at the chest.
        const c0 = this.chain[0] ?? { x: 0, y: 0 };
        const p = this.ctx.at(c0.x, c0.y, 0.4);
        this.ctx.shake(e.kind === 'wave' ? 0.3 : 0.15);
        this.ctx.flare(p, '#eaf6ff', 3.4, 14);
        this.ctx.vfx.burst(p, { n: low ? 14 : 36, color: '#eaf6ff', speed: 0.12, up: 0.15, size: 0.09, life: 24, gravity: 0.01, spread: 1.2 });
      } else if (e.kind === 'lost') {
        // A hiss of steam where the heart went under.
        const p = this.lastHeart.clone();
        this.ctx.flare(p, '#ff8a4a', 2.6, 12);
        this.ctx.vfx.burst(p, { n: low ? 6 : 16, color: '#e8e4e0', speed: 0.04, up: 0.08, size: 0.6, life: 40, smoke: true, spread: 0.5 });
        this.ctx.vfx.burst(p, { n: low ? 4 : 10, color: '#ff6a2a', speed: 0.05, up: 0.06, size: 0.05, life: 14, gravity: 0.01, spread: 0.4 });
      } else if (e.kind === 'out') {
        this.ctx.flare(this.rock.clone().setY(this.rock.y + 0.8), '#dfe9ff', 2.6, 16);
      }
    }
    const d = s.drink;
    // The heart dropped: a warm ring pulsing round it (it's urgent).
    if (d?.heart?.at === 'ground' && s.tick % 10 === 0) {
      const p = this.ctx.at(d.heart.x, d.heart.y, 0.05);
      this.ctx.vfx.ring(p, 0.2, 1.4, '#ff8a4a', 12, 0.8);
    }
    if (!d?.burst || d.front <= 0) return;
    const f = this.pointAt(d.front);
    const nx = -f.dy;
    const ny = f.dx;
    const n = low ? 3 : 7;
    for (let k = 0; k < n; k++) {
      const off = ((k / (n - 1)) * 2 - 1) * 2000;
      const p = this.ctx.at(f.p.x + nx * off, f.p.y + ny * off, 0.2);
      this.ctx.vfx.burst(p, { n: 1, color: k % 2 ? '#ffffff' : '#d8ecff', speed: 0.03, up: 0.08, size: 0.09, life: 12, gravity: 0.012, spread: 0.3 });
    }
    if (s.tick % 4 === 0) this.ctx.vfx.burst(this.ctx.at(f.p.x, f.p.y, 0.3), { n: 1, color: '#e6f2ff', speed: 0.01, up: 0.02, size: 0.8, life: 18, smoke: true, spread: 0.8 });
    for (const h of s.heroes) {
      if (h.downTicks > 0 || (s.tick + h.id) % (low ? 6 : 3) !== 0 || this.along(h.x, h.y) > d.front) continue;
      const p = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
      this.v.copy(p).setY(this.ctx.ground(p.x, p.z) + 0.2);
      this.ctx.vfx.burst(this.v, { n: low ? 2 : 4, color: '#dff0ff', speed: 0.03, up: 0.05, size: 0.06, life: 10, gravity: 0.012, spread: 0.35 });
    }
  }

  /** Every redraw: the water to its front, the heart where it is, its glow, the ink's glints, the way out on the rock. */
  draw(s: BattleState): void {
    const d = s.drink;
    this.water.visible = this.up && !!d?.burst && d.front > 0;
    this.waterM.uniforms.front!.value = d?.front ?? -1;
    this.waterM.uniforms.time!.value = this.now;
    // Where the heart is: in the chest (fight 1; fight 2 left there until the sea takes it), carried, dropped, gone.
    const hs = d?.heart;
    let shown = true;
    if (hs) {
      if (hs.at === 'lost') shown = false;
      else if (hs.at === 'carried') {
        const carrier = s.heroes.find((h) => h.pid === hs.pid);
        const p = carrier ? (this.ctx.pos(carrier.id) ?? this.ctx.at(carrier.x, carrier.y, 0)) : this.ctx.at(hs.x, hs.y, 0);
        this.heart.position.set(p.x, this.ctx.ground(p.x, p.z) + 1.95, p.z);
        this.heart.scale.setScalar(0.85);
      } else {
        const p = this.ctx.at(hs.x, hs.y, 0);
        this.heart.position.set(p.x, p.y + 0.3, p.z);
        this.heart.scale.setScalar(0.75);
      }
    } else {
      this.heart.position.set(this.chest.x, this.chest.y + 0.65, this.chest.z);
      this.heart.scale.setScalar(1.4);
      // Left in the chest in fight 2: the sea has it as soon as it comes in.
      if (this.up && d?.burst && d.front > 0) shown = false;
    }
    this.heart.visible = shown;
    if (shown) this.lastHeart.copy(this.heart.position);
    this.heart.rotation.y = this.now * 0.4;
    this.glow.begin();
    const beat = 0.5 + 0.5 * Math.sin(this.now * 2.4);
    if (shown) {
      const p = this.heart.position;
      const sc = this.heart.scale.x;
      this.glow.add(p.x, p.y, p.z, RED, 1.8 * sc, 0.14 + 0.08 * beat);
      this.glow.add(p.x, p.y, p.z, EMBER, 0.9 * sc, 0.3 + 0.2 * beat);
      this.glow.add(p.x, p.y, p.z, EMBER_CORE, 0.4 * sc, 0.5 + 0.3 * beat);
      this.glow.add(p.x, this.ctx.ground(p.x, p.z) + 0.05, p.z, RED, 1.6 * sc, 0.1 + 0.06 * beat);
    }
    this.ink.forEach((p, i) => {
      const t = 0.5 + 0.5 * Math.sin(this.now * 1.1 + i * 1.9);
      this.glow.add(p.x, p.y, p.z, INK_GLOW, 0.3, 0.18 * t);
    });
    if (this.up) {
      const w = 0.6 + 0.3 * Math.sin(this.now * 3);
      this.glow.add(this.rock.x, this.rock.y + 0.3, this.rock.z, COOL, 2.4, 0.16 * w);
    }
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
